import type { ZodSchema, ZodTypeDef } from 'zod';
import zod, { ZodError } from 'zod';

import type { Capability, ExternalLogger, Permission, ProductEnvironment, User, UserRole, UserTechnicalRole, IntegrationProvider } from '@cld/unicorn-api';
import type { CloudinaryFetchClient, ConsoleApiClient, ConsoleLogger } from '../state/types.js';

const planResponseSchema = zod.object({
  name: zod.string(),
  paid: zod.boolean(),
  display_name: zod.string(),
});

const accountResponseSchema = zod.object({
  name: zod.string(),
  company_name: zod.string().nullable(),
  created_at: zod.string().datetime(),
  external_id: zod.string(),
  id: zod.string(),
  new_billing_system: zod.boolean(),
  max_customers: zod.number(),
  max_users: zod.number(),
  num_customers: zod.number(),
  two_factor_required: zod.boolean(),
  email: zod.string(),
  contact_first_name: zod.string().nullable(),
  contact_last_name: zod.string().nullable(),
  can_use_user_addon_plans: zod.boolean(),
  eligible_for_media_library_permissions: zod.boolean(),
  eligible_for_new_navigation: zod.boolean().optional().nullable(),
  // FIXME: check if we can narrow this down to always present
  dynamic_folders_by_default: zod.boolean().optional().nullable(),
  kind: zod.string(),
});

const customerResponseSchema = zod.object({
  api_key: zod.string().nullable(),
  api_secret: zod.string().nullable(),
  created_at: zod.string().datetime(),
  cloud_name: zod.string(),
  id: zod.string(),
  external_id: zod.string(),
  display_name: zod.string(),
  account_id: zod.string(),
  strict_transformations: zod.boolean(),
  folder_decoupling_enabled: zod.boolean(),
  /** @deprecated This does not fit present naming / product identification. */
  product_name: zod.string(),
  cname: zod.string().nullable().optional(),
  base_url: zod.string().nullable().optional(),
  secure_base_url: zod.string().nullable().optional(),
  block_display_customer_images: zod.boolean().nullable().optional(),
  cdn_url_type: zod.string(),
  media_library_original_filename: zod.boolean().nullable().optional(),
  is_untrusted: zod.boolean().nullable().optional(),
  upload_limits: zod.object({
    asset_max_total_px: zod.number(),
    image_max_px: zod.number(),
    image_max_size_bytes: zod.number(),
    raw_max_size_bytes: zod.number(),
    video_max_size_bytes: zod.number(),
  }),
  programmable_media_editing: zod.boolean().optional().nullable(),
  media_management: zod.boolean().optional().nullable(),
  media_optimizer: zod.boolean().optional().nullable(),
  status: zod.string(),
});

const allCustomersResponseSchema = zod.object({
  data: zod.array(customerResponseSchema),
});

const gdprLogSchema = zod.object({});

// function isValidZodLiteralUnion<T extends zod.ZodLiteral<unknown>>(literals: T[]): literals is [T, T, ...T[]] {
//   return literals.length >= 2;
// }
// function constructZodLiteralUnionType<T extends zod.ZodLiteral<unknown>>(literals: T[]) {
//   if (!isValidZodLiteralUnion(literals)) {
//     throw new Error('Literals passed do not meet the criteria for constructing a union schema, the minimum length is 2');
//   }
//   return zod.union(literals);
// }

// const isIn = <ItemType>(values: ReadonlyArray<ItemType>, value: any): value is ItemType => values.includes(value);
// const zodFallbackUnion = <Fallback extends string, SupportedValues extends Array<string>>({
//   fallback,
//   supportedValues,
// }: {
//   fallback: Fallback;
//   supportedValues: SupportedValues;
// }) => {
//   return zod
//     .string()
//     .transform((value) => {
//       if (isIn(supportedValues, value)) {
//         return value;
//       }

//       return fallback;
//     })
//     .pipe(constructZodLiteralUnionType(supportedValues.map((value) => zod.literal(value))));
// };

const userResponseSchema = zod.object({
  first_name: zod.string().nullable(),
  last_name: zod.string().nullable(),
  name: zod.string().nullable(),
  id: zod.string(),
  external_id: zod.string(),
  admin: zod.boolean().optional(),
  can_setup_two_factor: zod.boolean(),
  can_view_reports: zod.boolean().optional(),
  created_at: zod.string().datetime(),
  // FIXME: check if this is nullable or not
  cloudinary_user: zod.boolean().nullable().optional(),
  two_factor: zod.boolean(),
  account_id: zod.string(),
  email: zod.string().nullable(),
  user_theme: zod.enum(['system', 'dark', 'light']).nullable(),
  setting_pages: zod.object({
    account_page: zod.boolean(),
    account_security_page: zod.boolean(),
    addons_page: zod.boolean(),
    billing_page: zod.boolean(),
    security_page: zod.boolean(),
    upload_page: zod.boolean(),
  }),
  email_subscriptions: zod.object({
    collection_invitation: zod.boolean(),
    comment_mention: zod.boolean(),
    folder_invitation: zod.boolean(),
    monthly_report: zod.boolean(),
    saved_search_invitation: zod.boolean(),
    creative_approval_reviewer: zod.boolean(),
    creative_approval_owner: zod.boolean(),
  }),
  // FIXME: fallback union neccessary, otherwise unknown values cause errors
  role: zod.string(),
  ml_permissions: zod
    .object({
      create_collection: zod.boolean().optional().nullable(),
      moderate_asset: zod.boolean().optional().nullable(),
      share_collection: zod.boolean().optional().nullable(),
      show_delivery_url: zod.boolean().optional().nullable(),
    })
    .optional()
    .nullable(),
  avatar_color_scheme: zod.object({
    background: zod.string(),
    text: zod.string(),
  }),
  intercom_hash: zod.string().nullable().optional(),
  plan_infos: zod
    .array(
      zod.object({
        account_name: zod.string(),
        paid: zod.boolean(),
        plan_name: zod.string(),
        plan_size: zod.string(),
        product: zod.string(),
        units_pricing: zod.boolean(),
      })
    )
    .nullable()
    .optional(),
  main_goals: zod
    .union([zod.array(zod.string()), zod.string()])
    .nullable()
    .optional(),
  main_goals_details: zod.string().nullable().optional(),
  technical_role: zod.string().nullable(),
  technical_role_details: zod.string().nullable().optional(),
  password_action: zod.string().nullable().optional(),
  providers: zod.array(zod.string()).nullable().optional(),
  user_effective_language: zod.string().nullable().optional(),
});

const sessionNotificationsSchema = zod.object({
  email_notification: zod.object({ reason: zod.union([zod.literal('email_not_verified'), zod.literal('email_verification_required')]) }).nullable(),
  billing_notification: zod
    .object({
      reason: zod.literal('missing_billing_information'),
      data: zod
        .object({
          billing_settings_path: zod.string().optional(),
        })
        .optional(),
    })
    .nullable(),
  disabled_entity: zod.union([
    zod.object({
      reason: zod.union([
        zod.literal('account_disabled'),
        zod.literal('account_cancelled'),
        zod.literal('email_address_not_verified'),
        zod.literal('customer_disabled'),
      ]),
    }),
    zod.null(),
  ]),
  invoice_notification: zod
    .object({
      reason: zod.literal('invoices_overdue'),
      data: zod.union([
        zod.object({
          request_payment_update: zod.literal(true),
          payment_settings_link: zod.string(),
        }),
        zod.object({
          request_payment_update: zod.literal(false),
        }),
      ]),
    })
    .nullable(),
});

const loginFromSessionResponseSchema = zod.object({
  // FIXME: we need a whole list + fallback union neccessary, otherwise unknown values cause errors
  capabilities: zod.array(zod.string()),
  customer: customerResponseSchema,
  user: userResponseSchema,

  jwt: zod.string(),
  cms_mode: zod.boolean().optional(),
  session_notifications: sessionNotificationsSchema,
  // FIXME: add more precision to the type
  user_events_latest: zod.array(
    zod.object({
      id: zod.string(),
      kind: zod.string(),
      metadata: zod.unknown().optional().nullable(),
    })
  ),
  // FIXME: we need a whole list + fallback union neccessary, otherwise unknown values cause errors
  permissions: zod.array(zod.string()),
  // FIXME: we need a whole list + fallback union neccessary, otherwise unknown values cause errors
  admin_permissions: zod.array(zod.string()).optional(),
  // FIXME: need a fallback union
  root_folder_role: zod.string().nullable().optional(),
  intercom_hash: zod.string().nullable().optional(),
});

const capabilityMap: Record<string, Capability> = {
  access_control: 'accessControl',
  allow_custom_video_presets: 'allowCustomVideoPresets',
  elastic_backend: 'elasticBackend',
  folders_tree_enabled: 'foldersTreeEnabled',
  transformation_builder_cloud_enabled: 'transformationBuilderCloudEnabled',
  moderation_in_elastic: 'moderationInElastic',
  is_untrusted: 'isUntrusted',
  folders_in_grid: 'foldersInGrid',
  multiple_users: 'multipleUsers',
  asset_collections: 'assetCollections',
  force_new_ml: 'forceNewMl',
  deprecate_ml_original_filename_setting: 'deprecateMlOriginalFilenameSetting',
  brand_portals: 'brandPortals',
  new_transformations_page: 'newTransformationsPage',
  transformation_builder_enabled: 'transformationBuilderEnabled',
  media_library_localization_enabled: 'mediaLibraryLocalizationEnabled',
  billing_dashboard_page: 'billingDashboardPage',
  getting_started_page_enabled: 'gettingStartedPageEnabled',
  creative_automation_enabled: 'creativeAutomationEnabled',
  media_library_permissions: 'mediaLibraryPermissions',
  transformations_presets_enabled: 'transformationsPresetsEnabled',
  new_video_reports_page: 'newVideoReportsPage',
  new_error_reports_page: 'newErrorReportsPage',
  new_developer_dashboard_enabled: 'newDeveloperDashboardEnabled',
  transformation_builder_ai_assistant_enabled: 'transformationBuilderAiAssistantEnabled',
  ai_transformation_refinement: 'aiTransformationRefinement',
  cdn_acl_settings_enabled: 'cdnAclSettingsEnabled',
  cname_settings_enabled: 'cnameSettingsEnabled',
  new_video_app: 'newVideoApp',
  media_explorer_disabled: 'mediaExplorerDisabled',
};

const permissionMap: Record<string, Permission> = {
  modify_account_settings: 'modifyAccountSettings',
  modify_cloud_settings: 'modifyCloudSettings',
  manage_users: 'manageUsers',
  manage_images: 'manageImages',
  manage_transformations: 'manageTransformations',
  view_reports: 'viewReports',
  view_access_keys: 'viewAccessKeys',
  manage_moderations: 'manageModerations',
  modify_provisioning_settings: 'modifyProvisioningSettings',
  read_images: 'readImages',
  contribute: 'contribute',
  modify_account_authentication: 'modifyAccountAuthentication',
  receive_product_emails: 'receiveProductEmails',
  administrate_ml: 'administrateMl',
  use_ml: 'useMl',
  modify_account_preferences: 'modifyAccountPreferences',
  view_billing_dashboard: 'viewBillingDashboard',
  view_account_audit_log: 'viewAccountAuditLog',
  manage_permissions: 'managePermissions',
  browse_accounts: 'browseAccounts',
  edit_accounts_general_info: 'editAccountsGeneralInfo',
  operate_accounts_standard_technical_actions: 'operateAccountsStandardTechnicalActions',
  login_as_accounts: 'loginAsAccounts',
  manage_public_plans: 'managePublicPlans',
  edit_blog_posts: 'editBlogPosts',
  publish_blog_posts: 'publishBlogPosts',
  view_system_stats: 'viewSystemStats',
  view_accounting_reports: 'viewAccountingReports',
  manage_site_people: 'manageSitePeople',
  manage_product_updates: 'manageProductUpdates',
  delete_accounts: 'deleteAccounts',
  view_feature_rollouts: 'viewFeatureRollouts',
  edit_feature_rollouts: 'editFeatureRollouts',
  designate_admins: 'designateAdmins',
  edit_promo_codes: 'editPromoCodes',
  modify_account_technical_settings: 'modifyAccountTechnicalSettings',
  use_advanced_billing: 'useAdvancedBilling',
  see_account_images: 'seeAccountImages',
  see_account_reports: 'seeAccountReports',
  see_last_four_digits_of_api_secret: 'seeLastFourDigitsOfApiSecret',
  view_account_details: 'viewAccountDetails',
  manage_account_plans_and_subscriptions: 'manageAccountPlansAndSubscriptions',
  operate_accounts_destructive: 'operateAccountsDestructive',
  edit_cookbook_recipes: 'editCookbookRecipes',
  publish_cookbook_recipes: 'publishCookbookRecipes',
  manage_account_groups: 'manageAccountGroups',
  modify_to_custom_service_limits: 'modifyToCustomServiceLimits',
  block_image_view_for_cld_users: 'blockImageViewForCldUsers',
};

const userRoleMap: Record<string, UserRole> = {
  admin: 'admin',
  billing: 'billing',
  editor: 'editor',
  master_admin: 'masterAdmin',
  media_library_admin: 'mediaLibraryAdmin',
  media_library_user: 'mediaLibraryUser',
  reports: 'reports',
  technical_admin: 'technicalAdmin',
};

const userTechnicalRoleMap: Record<string, UserTechnicalRole> = {
  developer: 'developer',
  deveops: 'devops',
  e_commerce_manager: 'eCommerceManager',
  media_manager: 'mediaManager',
  other: 'other',
  tech_exec: 'technicalExecutive',
  creative: 'creative',
};

const productNameMap: Record<string, ProductEnvironment['productName']> = {
  classic: 'classic',
  media_optimizer: 'mediaOptimizer',
};

const cdnUrlTypeMap: Record<string, ProductEnvironment['cdnUrlType']> = {
  domain: 'domain',
  component: 'component',
  subdomains: 'subdomains',
};

const productEnvironmentStatusMap: Record<string, ProductEnvironment['status']> = {
  active: 'active',
  disabled: 'disabled',
};

const passwordActionMap: Record<string, User['passwordAction']> = {
  set_direct_access: 'setDirectAccess',
  password_not_allowed: 'passwordNotAllowed',
  set_password: 'setPassword',
  change_password: 'changePassword',
};

const authenticationProvidersMap: Record<string, User['authenticationProviders'][number]> = {
  password: 'password',
  google: 'google',
  github: 'github',
  sso: 'sso',
};

const accountIntegrationProviderMap: Record<string, IntegrationProvider> = {
  direct: 'direct',
  heroku: 'heroku',
  cloudcontrol: 'cloudcontrol',
  appfog: 'appfog',
  engine_yard: 'engineYard',
  azure: 'azure',
  appharbor: 'appharbor',
  appdirect: 'appdirect',
  aws_marketplace: 'awsMarketplace',
  aws_tackle: 'awsTackle',
};

const knownRootFolderRoles = ['viewer', 'contributor', 'editor', 'manager', 'admin', 'by_proxy'] as const;
const parseRootFolderRole = (rootFolderRole: string | null | undefined) => {
  if (!rootFolderRole) {
    return undefined;
  }

  return knownRootFolderRoles.find((role) => role === rootFolderRole) ?? 'UNKNOWN_VALUE';
};

export const createConsoleApiClient = ({
  backendUrl,
  cloudinaryFetchClient,
  consoleLogger,
  unicornExternalLogger,
}: {
  backendUrl: string;
  cloudinaryFetchClient: CloudinaryFetchClient;
  consoleLogger: ConsoleLogger;
  unicornExternalLogger: ExternalLogger;
}): ConsoleApiClient => {
  const createRequest =
    <Method extends 'GET' | 'POST', Output, Definition extends ZodTypeDef, Input, Schema extends ZodSchema<Output, Definition, Input>, TransformedData>(
      method: Method,
      path: string,
      schema: Schema,
      baseErrorPath: string,
      transformer: (data: zod.infer<Schema>) => TransformedData,
      body?: Method extends 'GET' ? never : BodyInit
    ) =>
    async () => {
      try {
        const options: RequestInit = {
          method,
          headers: { 'Content-Type': 'application/json' },
          ...(method === 'POST' && body ? { body } : {}),
        };

        const response = await cloudinaryFetchClient(`${backendUrl}${path}`, options);

        if (!response.ok) {
          if (response.text) {
            const errorMessage = await response.text();
            throw new Error(`Status: ${response.status} - ${errorMessage}`);
          }

          throw new Error(`Status: ${response.status} - could not parse error response as text`);
        }

        const responseJson = await response.json();
        const data = await schema.parseAsync(responseJson, { path: [baseErrorPath] });

        consoleLogger.debug('api', 'Request success', { data, url: response.url });

        return { kind: 'success' as const, data: transformer(data) };
      } catch (error) {
        if (error instanceof ZodError) {
          consoleLogger.error('api', 'Schema validation error', error.issues as unknown as Array<Record<string, unknown>>);
          unicornExternalLogger.error(error);
          return { kind: 'error' as const, errorMessage: 'Invalid response' };
        }

        if (error instanceof Error) {
          consoleLogger.error('api', 'Request error', { error });
          unicornExternalLogger.error(error);
          return { kind: 'error' as const, errorMessage: error.message };
        }

        consoleLogger.error('api', 'Unknown error', { error });
        unicornExternalLogger.error('Unknown error');
        return { kind: 'error' as const, errorMessage: 'Unknown error' };
      }
    };

  return {
    fetchAccount: createRequest('GET', '/account', accountResponseSchema, 'accountRequest', (data) => ({
      createdAt: new Date(data.created_at),
      __MIGRATION_createdAt: data.created_at,
      externalId: data.external_id,
      id: data.id,
      newBillingSystem: data.new_billing_system,
      name: data.name,
      companyName: data.company_name ?? undefined,
      maxNumberOfCustomers: data.max_customers,
      maxNumberOfUsers: data.max_users,
      currentNumberOfCustomers: data.num_customers,
      twoFactorAuthorizationRequired: data.two_factor_required,
      email: data.email,
      contactFirstName: data.contact_first_name ?? undefined,
      contactLastName: data.contact_last_name ?? undefined,
      canUseUserAddonPlans: data.can_use_user_addon_plans,
      eligibleForMediaLibraryPermissions: data.eligible_for_media_library_permissions,
      eligibleForNewNavigation: data.eligible_for_new_navigation ?? false,
      dynamicFolderByDefault: data.dynamic_folders_by_default ?? false,
      integrationProvider: accountIntegrationProviderMap[data.kind] ?? 'UNKNOWN_VALUE',
    })),
    // FIXME: update to reuse transforming logic from subtypes like account, product environment or user
    fetchLoginFromSession: createRequest('GET', '/auth/login_from_session', loginFromSessionResponseSchema, 'loginFromSessionRequest', (data) => ({
      capabilities: data.capabilities
        .filter((capability) => {
          const isKnown = Object.keys(capabilityMap).includes(capability);
          return isKnown;
        })
        .map((capability) => capabilityMap[capability]),
      productEnvironment: {
        apiKey: data.customer.api_key ?? undefined,
        apiSecret: data.customer.api_secret ?? undefined,
        cloudName: data.customer.cloud_name,
        createdAt: new Date(data.customer.created_at),
        externalId: data.customer.external_id,
        id: data.customer.id,
        displayName: data.customer.display_name,
        accountId: data.customer.account_id,
        strictTransformations: data.customer.strict_transformations,
        productName: productNameMap[data.customer.product_name] ?? 'UNKNOWN_VALUE',
        folderDecouplingEnabled: data.customer.folder_decoupling_enabled,
        cname: data.customer.cname ?? undefined,
        baseUrl: data.customer.base_url ?? undefined,
        secureBaseUrl: data.customer.secure_base_url ?? undefined,
        blockDisplayCustomerImages: data.customer.block_display_customer_images ?? false,
        cdnUrlType: cdnUrlTypeMap[data.customer.cdn_url_type] ?? 'UNKNOWN_VALUE',
        mediaLibraryUseOriginalFilename: data.customer.media_library_original_filename ?? false,
        isUntrusted: data.customer.is_untrusted ?? false,
        uploadMaximumLimits: {
          singleframeImagePixels: data.customer.upload_limits.image_max_px,
          multiframeImagePixels: data.customer.upload_limits.asset_max_total_px,
          imageBytes: data.customer.upload_limits.image_max_size_bytes,
          rawBytes: data.customer.upload_limits.raw_max_size_bytes,
          videoBytes: data.customer.upload_limits.video_max_size_bytes,
        },
        enabledProducts: [
          ...(data.customer.programmable_media_editing ? (['programmableMediaEditing'] as const) : []),
          ...(data.customer.media_management ? (['mediaManagement'] as const) : []),
          ...(data.customer.media_optimizer ? (['mediaOptimizer'] as const) : []),
        ],
        status: productEnvironmentStatusMap[data.customer.status] ?? 'UNKNOWN_VALUE',
      },
      user: {
        firstName: data.user.first_name ?? undefined,
        lastName: data.user.last_name ?? undefined,
        fullName: data.user.name ?? undefined,
        createdAt: new Date(data.user.created_at),
        id: data.user.id,
        externalId: data.user.external_id,
        accountId: data.user.account_id,
        role: userRoleMap[data.user.role] ?? 'UNKNOWN_VALUE',
        isCloudinaryAdminEmployee: Boolean(data.user.admin),
        isCloudinaryEmployee: Boolean(data.user.cloudinary_user),
        userTheme: data.user.user_theme ?? 'system',
        canSetupTwoFactor: data.user.can_setup_two_factor,
        twoFactorAuthorizationEnabled: data.user.two_factor,
        email: data.user.email,
        canViewReports: !!data.user.can_view_reports,
        emailSubscriptions: [
          ...(data.user.email_subscriptions.collection_invitation ? (['collectionInvitation'] as const) : []),
          ...(data.user.email_subscriptions.comment_mention ? (['commentMention'] as const) : []),
          ...(data.user.email_subscriptions.folder_invitation ? (['folderInvitation'] as const) : []),
          ...(data.user.email_subscriptions.monthly_report ? (['monthlyReport'] as const) : []),
          ...(data.user.email_subscriptions.saved_search_invitation ? (['savedSearchInvitation'] as const) : []),
          ...(data.user.email_subscriptions.creative_approval_reviewer ? (['creativeApprovalReviewer'] as const) : []),
          ...(data.user.email_subscriptions.creative_approval_owner ? (['creativeApprovalOwner'] as const) : []),
        ],
        avatarBackgroundColour: data.user.avatar_color_scheme.background,
        avatarTextColour: data.user.avatar_color_scheme.text,
        intercomHash: data.intercom_hash ?? undefined,
        planInfos: [],
        technicalRole: data.user.technical_role ? (userTechnicalRoleMap[data.user.technical_role] ?? 'UNKNOWN_VALUE') : undefined,
        technicalRoleDetails: data.user.technical_role_details ?? '',
        mainGoals: Array.isArray(data.user.main_goals) ? data.user.main_goals : data.user.main_goals ? [data.user.main_goals] : [],
        mainGoalsDetails: data.user.main_goals_details ?? '',
        language: data.user.user_effective_language ?? 'en-US',
        passwordAction: data.user.password_action ? passwordActionMap[data.user.password_action] : undefined,
        authenticationProviders: (data.user.providers ?? []).map((provider) => authenticationProvidersMap[provider]),
      },
      permissions: [...data.permissions, ...(data.admin_permissions ?? [])]
        .filter((permission) => {
          const isKnown = Object.keys(permissionMap).includes(permission);

          if (!isKnown) {
            consoleLogger.warning('api', 'Unknown permission received', { permission });
          }

          return isKnown;
        })
        .map((permission) => permissionMap[permission])
        // FIXME: this should probably just be normal permissions on the backend, ask Eitan Revach
        .concat([
          ...(data.user.setting_pages.account_page ? (['viewSettingsPageAccountPage'] as const) : []),
          ...(data.user.setting_pages.account_security_page ? (['viewSettingsPageAccountSecurityPage'] as const) : []),
          ...(data.user.setting_pages.addons_page ? (['viewSettingsPageAddonsPage'] as const) : []),
          ...(data.user.setting_pages.billing_page ? (['viewSettingsPageBillingPage'] as const) : []),
          ...(data.user.setting_pages.security_page ? (['viewSettingsPageSecurityPage'] as const) : []),
          ...(data.user.setting_pages.upload_page ? (['viewSettingsPageUploadPage'] as const) : []),
        ]),
      jwt: data.jwt,
      cmsMode: Boolean(data.cms_mode),
      userEvents: data.user_events_latest.map((event) => {
        if (event.metadata && typeof event.metadata !== 'object') {
          consoleLogger.warning('general', 'User event with non-object metadata found - ignoring content', { userEvent: event });
        }

        return {
          id: event.id,
          kind: event.kind,
          metadata: event.metadata && typeof event.metadata === 'object' ? (event.metadata as Record<string, unknown>) : undefined,
        };
      }),
      rootFolderRole: parseRootFolderRole(data.root_folder_role),
      intercomHash: data.intercom_hash ?? undefined,
      sessionNotifications: {
        emailNotification: data.session_notifications?.email_notification ?? undefined,
        invoiceNotification: data.session_notifications?.invoice_notification
          ? {
              reason: data.session_notifications.invoice_notification.reason,
              data: data.session_notifications.invoice_notification.data.request_payment_update
                ? {
                    requestPaymentUpdate: data.session_notifications.invoice_notification.data.request_payment_update,
                    paymentSettingsLink: data.session_notifications.invoice_notification.data.payment_settings_link,
                  }
                : { requestPaymentUpdate: data.session_notifications.invoice_notification.data.request_payment_update },
            }
          : undefined,
        billingNotification: data.session_notifications?.billing_notification
          ? {
              reason: data.session_notifications.billing_notification.reason,
              data: {
                billingSettingsPath: data.session_notifications.billing_notification.data?.billing_settings_path,
              },
            }
          : undefined,
        disabledEntity: data.session_notifications.disabled_entity
          ? {
              reason: data.session_notifications.disabled_entity.reason,
            }
          : undefined,
      },
      mediaLibraryPermissions: {
        create_collection: data.user.ml_permissions?.create_collection ?? false,
        moderate_asset: data.user.ml_permissions?.moderate_asset ?? false,
        share_collection: data.user.ml_permissions?.share_collection ?? false,
        show_delivery_url: data.user.ml_permissions?.show_delivery_url ?? false,
      },
    })),
    fetchPlan: createRequest('GET', '/account/plan', planResponseSchema, 'accountPlanRequest', (data) => ({
      name: data.name,
      paid: data.paid,
      displayName: data.display_name,
    })),
    fetchUser: createRequest('GET', '/user', userResponseSchema, 'userRequest', (data) => ({
      firstName: data.first_name ?? undefined,
      id: data.id,
      createdAt: new Date(data.created_at),
      accountId: data.account_id,
      externalId: data.external_id,
      lastName: data.last_name ?? undefined,
      role: userRoleMap[data.role] ?? 'UNKNOWN_VALUE',
      isCloudinaryAdminEmployee: Boolean(data.admin),
      isCloudinaryEmployee: Boolean(data.cloudinary_user),
      canSetupTwoFactor: data.can_setup_two_factor,
      twoFactorAuthorizationEnabled: data.two_factor,
      email: data.email,
      fullName: data.name ?? undefined,
      canViewReports: !!data.can_view_reports,
      userTheme: data.user_theme ?? 'system',
      emailSubscriptions: [
        ...(data.email_subscriptions.collection_invitation ? (['collectionInvitation'] as const) : []),
        ...(data.email_subscriptions.comment_mention ? (['commentMention'] as const) : []),
        ...(data.email_subscriptions.folder_invitation ? (['folderInvitation'] as const) : []),
        ...(data.email_subscriptions.monthly_report ? (['monthlyReport'] as const) : []),
        ...(data.email_subscriptions.saved_search_invitation ? (['savedSearchInvitation'] as const) : []),
        ...(data.email_subscriptions.creative_approval_reviewer ? (['creativeApprovalReviewer'] as const) : []),
        ...(data.email_subscriptions.creative_approval_owner ? (['creativeApprovalOwner'] as const) : []),
      ],
      avatarBackgroundColour: data.avatar_color_scheme.background,
      avatarTextColour: data.avatar_color_scheme.text,
      intercomHash: data.intercom_hash ?? undefined,
      planInfos: (data.plan_infos ?? []).map((planInfo) => ({
        paid: planInfo.paid,
        name: planInfo.plan_name,
        size: planInfo.plan_size,
        unitsPricing: planInfo.units_pricing,
        product: planInfo.product,
        accountName: planInfo.account_name,
      })),
      technicalRole: data.technical_role ? (userTechnicalRoleMap[data.technical_role] ?? 'UNKNOWN_VALUE') : undefined,
      technicalRoleDetails: data.technical_role_details ?? '',
      mainGoals: Array.isArray(data.main_goals) ? data.main_goals : data.main_goals ? [data.main_goals] : [],
      mainGoalsDetails: data.main_goals_details ?? '',
      language: data.user_effective_language ?? 'en-US',
      passwordAction: data.password_action ? passwordActionMap[data.password_action] : undefined,
      authenticationProviders: (data.providers ?? []).map((provider) => authenticationProvidersMap[provider]),
    })),
    fetchAllProductEnvironments: () =>
      // FIXME: ask BE developer about the per_page setting
      // FIXME: consider refactoring createRequest() to pass query separately
      createRequest('GET', '/customers?per_page=50', allCustomersResponseSchema, 'allCustomersRequest', ({ data }) =>
        data.map((customer) => ({
          apiKey: customer.api_key ?? undefined,
          apiSecret: customer.api_secret ?? undefined,
          cloudName: customer.cloud_name,
          createdAt: new Date(customer.created_at),
          accountId: customer.account_id,
          externalId: customer.external_id,
          id: customer.id,
          displayName: customer.display_name,
          strictTransformations: customer.strict_transformations,
          productName: productNameMap[customer.product_name] ?? 'UNKNOWN_VALUE',
          folderDecouplingEnabled: customer.folder_decoupling_enabled,
          cname: customer.cname ?? undefined,
          baseUrl: customer.base_url ?? undefined,
          secureBaseUrl: customer.secure_base_url ?? undefined,
          blockDisplayCustomerImages: customer.block_display_customer_images ?? false,
          cdnUrlType: cdnUrlTypeMap[customer.cdn_url_type] ?? 'UNKNOWN_VALUE',
          mediaLibraryUseOriginalFilename: customer.media_library_original_filename ?? false,
          isUntrusted: customer.is_untrusted ?? false,
          uploadMaximumLimits: {
            singleframeImagePixels: customer.upload_limits.image_max_px,
            multiframeImagePixels: customer.upload_limits.asset_max_total_px,
            imageBytes: customer.upload_limits.image_max_size_bytes,
            rawBytes: customer.upload_limits.raw_max_size_bytes,
            videoBytes: customer.upload_limits.video_max_size_bytes,
          },
          enabledProducts: [
            ...(customer.programmable_media_editing ? (['programmableMediaEditing'] as const) : []),
            ...(customer.media_management ? (['mediaManagement'] as const) : []),
            ...(customer.media_optimizer ? (['mediaOptimizer'] as const) : []),
          ],
          status: productEnvironmentStatusMap[customer.status] ?? 'UNKNOWN_VALUE',
        }))
      )(),
    fetchProductEnvironment: (externalId) =>
      createRequest('GET', `/customers/extid:${externalId}`, customerResponseSchema, 'productEnvironmentRequest', (data) => ({
        apiKey: data.api_key ?? undefined,
        apiSecret: data.api_secret ?? undefined,
        cloudName: data.cloud_name,
        createdAt: new Date(data.created_at),
        accountId: data.account_id,
        externalId: data.external_id,
        id: data.id,
        displayName: data.display_name,
        strictTransformations: data.strict_transformations,
        productName: productNameMap[data.product_name] ?? 'UNKNOWN_VALUE',
        folderDecouplingEnabled: data.folder_decoupling_enabled,
        cname: data.cname ?? undefined,
        baseUrl: data.base_url ?? undefined,
        secureBaseUrl: data.secure_base_url ?? undefined,
        blockDisplayCustomerImages: data.block_display_customer_images ?? false,
        cdnUrlType: cdnUrlTypeMap[data.cdn_url_type] ?? 'UNKNOWN_VALUE',
        mediaLibraryUseOriginalFilename: data.media_library_original_filename ?? false,
        isUntrusted: data.is_untrusted ?? false,
        uploadMaximumLimits: {
          singleframeImagePixels: data.upload_limits.image_max_px,
          multiframeImagePixels: data.upload_limits.asset_max_total_px,
          imageBytes: data.upload_limits.image_max_size_bytes,
          rawBytes: data.upload_limits.raw_max_size_bytes,
          videoBytes: data.upload_limits.video_max_size_bytes,
        },
        enabledProducts: [
          ...(data.programmable_media_editing ? (['programmableMediaEditing'] as const) : []),
          ...(data.media_management ? (['mediaManagement'] as const) : []),
          ...(data.media_optimizer ? (['mediaOptimizer'] as const) : []),
        ],
        status: productEnvironmentStatusMap[data.status] ?? 'UNKNOWN_VALUE',
      }))(),
    postGdprLog: (kind, subkind, metadata, details, justification) =>
      createRequest(
        'POST',
        '/gdpr_logs',
        gdprLogSchema,
        'postGdprLogRequest',
        () => undefined,
        JSON.stringify({
          kind,
          subkind,
          metadata: `${metadata} - ${justification}: ${details}`,
        })
      )(),
  };
};
